import { useLazyQuery, useMutation } from '@apollo/client';
import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { cloneDeep } from 'lodash';
import { toast } from 'react-toastify';
import { MAIN_THEME } from '@styles';
import { IAmendmentGeneralData } from '../../line-amendment';
import { confirmAmendmentMutation, getClausesListQuery } from './queries';
import { IClauseOption } from '.';
import { isEmpty } from '@utils/validationUtils';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { downloadFileFromUrl, generateDownloadLink } from '@utils/file-utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { getError } from '@utils/graph-utils';
import EnhancedDialog from '@components/common/EnhancedDialog';
import Loader from '@components/loader/Loader';
import SelectFormField from '@components/form-fields/SelectFormField';
import EnhancedButton from '@components/button/EnhancedButton';
import { extractClauseOptions, getFileDetailsList } from './utils';
import { LineCategory } from '@utils/helper-utils';
import TextEditorFormField from '@components/form-fields/TextEditorFormField';

const useStyles = makeStyles()(() => ({
  container: {
    width: '100%',
    minHeight: '529px',
  },
  body: {
    width: '100%',
    backgroundColor: MAIN_THEME.color.white,
    boxSizing: 'border-box',
  },
  loader: {
    width: '80px',
    height: '80px',
  },
  loaderContainer: {
    textAlign: 'center',
  },
  main: {
    width: '100%',
    minHeight: '500px',
    padding: '35px 25px 25px',
  },
  title: {
    display: 'block',
    minWidth: 'min-content',
    width: '100%',
    margin: '0 auto',
    textAlign: 'center',
    fontFamily: MAIN_THEME.font.bold,
    fontSize: `${MAIN_THEME.fontSize.md} !important`,
  },
  textContainer: {
    width: '100%',
  },
  field: {
    width: '100%',
    margin: '0 0 20px',
  },
  buttonsContainer: {
    width: '95%',
    margin: '10px auto 0',
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'center',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '15px',
  },
}));

interface IPreviewClauseModalProps {
  generalData: IAmendmentGeneralData;
  onClose?: () => void;
  onSuccess?: () => void;
}

interface IPageState {
  values: {
    clauseId: string;
    text: string;
  };
  errors: Record<string, string>;
  touched: Record<string, boolean>;
}

const PreviewClauseModal: React.FC<IPreviewClauseModalProps> = ({
  generalData,
  onClose = () => undefined,
  onSuccess,
}) => {
  const currentStatus = generalData.amendment.status;
  const lineId = generalData.line.id;
  const { classes } = useStyles();

  const [loader, setLoader] = useState<boolean>(true);

  const [clauseListQuery] = useLazyQuery(getClausesListQuery);

  const [clauses, setClauses] = useState<IClauseOption[]>([]);

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitAction] = useMutation(confirmAmendmentMutation);

  const [pageState, setPageState] = useState<IPageState>({
    values: {
      clauseId: '',
      text: generalData.policy.clauseText,
    },
    errors: {},
    touched: {},
  });

  const clausesList = useMemo(() => {
    const result: Record<string, string> = {};
    clauses.forEach((c) => {
      result[c.id] = `${c.externalCode} - ${c.name}`;
    });
    return result;
  }, [clauses]);

  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    const data = await clauseListQuery({
      variables: {
        pagination: {
          pageNumber: 1,
          pageSize: 1000,
        },
        selectedLineIds: lineId,
      },
      errorPolicy: 'all',
    });

    setClauses(extractClauseOptions(data));
    setLoader(false);
  };

  const onFieldUpdate = (
    fieldName: keyof IPageState['values'],
    value: string
  ) => {
    const newPageState = cloneDeep(pageState);
    newPageState.values[fieldName] = value;
    newPageState.errors[fieldName] = validateField(fieldName, value);
    setPageState(newPageState);
  };

  const onFieldBlur = (fieldName: string) => {
    const newPageState = cloneDeep(pageState);
    newPageState.touched[fieldName] = true;
    setPageState(newPageState);
  };

  const validateField = (
    fieldName: keyof IPageState['values'],
    value: string
  ) => {
    if (fieldName === 'text') {
      if (isEmpty(value)) {
        return 'Required';
      }
    }
    return '';
  };

  const validateForm = () => {
    const newPageState = cloneDeep(pageState);
    setPageState(newPageState);
    newPageState.errors = {
      clauseId: validateField('clauseId', pageState.values.clauseId),
      text: validateField('text', pageState.values.text),
    };

    newPageState.touched = {
      clauseId: true,
      text: true,
    };

    setPageState(newPageState);

    return Object.values(newPageState.errors).every((e) => isEmpty(e));
  };

  const submit = async () => {
    const isValid = validateForm();

    if (isValid) {
      if (!submitting) {
        setSubmitting(true);
        const result = await submitAction({
          variables: {
            selectedAmendmentID: generalData.amendment.id,
            selectedPolicyVersionID: generalData.policy.versionId,
            selectedPolicyID: generalData.policy.id,
            clauseText: generalData.policy.clauseText,
          },
          errorPolicy: 'all',
        });

        if (isEmpty(result.errors)) {
          toast.success(
            <ToastSuccessMessage>
              Amendment successfully confirmed.
            </ToastSuccessMessage>
          );

          if (
            [LineCategory.Motor, LineCategory.Expat].includes(
              generalData.line.category
            )
          ) {
            const filesInfo = getFileDetailsList(result.data);
            for (const fileInfo of filesInfo) {
              const downloadLink = generateDownloadLink(
                fileInfo.fileId,
                fileInfo.EntityTypeId,
                fileInfo.EntityId,
                fileInfo.EntityViewId,
                fileInfo.PropertyId
              );
              await downloadFileFromUrl(downloadLink, fileInfo.fileName);
            }
          }

          onSuccess();
          setSubmitting(false);
          onClose();
        } else {
          setSubmitting(false);
          toast.error(
            <ToastErrorMessage>{getError(result)}</ToastErrorMessage>
          );
        }
      }
    }
  };

  const currentClause = clauses.find((c) => c.id === pageState.values.clauseId);

  return (
    <EnhancedDialog onClose={onClose} open={true} className={classes.container}>
      <div className={classes.body}>
        {loader ? (
          <div className={classes.loaderContainer}>
            <Loader className={classes.loader} />
          </div>
        ) : (
          <div className={classes.main}>
            <h2 className={classes.title}>Preview Clause</h2>

            <div className={classes.textContainer}>
              <SelectFormField
                name="clauses"
                title="Insert Clause"
                value={pageState.values.clauseId || ''}
                onChange={(e) => {
                  onFieldUpdate('clauseId', e);
                }}
                onBlur={() => {
                  onFieldBlur('clauseId');
                }}
                disabled={
                  submitting ||
                  !(
                    generalData.loggedInUser.allowClauseEdits &&
                    generalData.plan.isClauseEditable
                  )
                }
                selectOptions={clausesList}
                error={pageState.errors.clauseId}
              />
            </div>

            <TextEditorFormField
              name="text"
              title="Description*"
              value={pageState.values.text || ''}
              onChange={(_, v) => {
                onFieldUpdate('text', v);
              }}
              onBlur={() => {
                onFieldBlur('text');
              }}
              htmlToAdd={currentClause?.description}
              error={pageState.touched.text ? pageState.errors.text : ''}
              disabled={
                submitting ||
                !(
                  generalData.loggedInUser.allowClauseEdits &&
                  generalData.plan.isClauseEditable
                )
              }
              className={classes.field}
              placeholder={''}
              addButton={true}
            />

            {
              <div className={classes.buttonsContainer}>
                <EnhancedButton disabled={submitting} onClick={() => onClose()}>
                  Cancel
                </EnhancedButton>

                {currentStatus !== 'NEW' && (
                  <EnhancedButton
                    state={submitting ? 'loading' : undefined}
                    isPrimary
                    disabled={submitting}
                    onClick={() => submit()}
                  >
                    Confirm
                  </EnhancedButton>
                )}
              </div>
            }
          </div>
        )}
      </div>
    </EnhancedDialog>
  );
};

export default PreviewClauseModal;
